import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { BaseChart } from "src/components/charts/BaseOptionChart";

// Peak-hour demand as a bar graph: total sessions by hour of day (0-23),
// summed across the period.

const PeakHourBar = ({
  heatmap,
  isLoading = false,
  height = { xs: 280, sm: 320, md: 340 },
}) => {
  const theme = useTheme();
  const { labels, totals, max } = useMemo(() => {
    const matrix = Array.isArray(heatmap?.data) ? heatmap.data : [];
    const hourTotals = new Array(24).fill(0);
    matrix.forEach((row) => {
      (row || []).forEach((v, h) => {
        if (h < 24) hourTotals[h] += Number(v) || 0;
      });
    });
    const lbls = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`);
    return { labels: lbls, totals: hourTotals, max: Math.max(...hourTotals, 0) };
  }, [heatmap]);

  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      },
      plotOptions: {
        bar: {
          columnWidth: "65%",
          borderRadius: 4,
          distributed: true, // per-bar colors so peaks stand out
        },
      },
      // Peak hours (>=80% of max) in deep blue, the rest lighter.
      // Theme chart blue: darker for peak hours, lighter otherwise.
      colors: totals.map((v) =>
        max > 0 && v >= 0.8 * max
          ? theme.palette.chart.blue[0]
          : theme.palette.chart.blue[2]
      ),
      dataLabels: { enabled: false },
      legend: { show: false },
      xaxis: {
        categories: labels,
        labels: {
          // Every 3rd hour, unrotated — 24 slanted labels are hard to read.
          rotate: 0,
          hideOverlappingLabels: false,
          formatter: (v) => (parseInt(v, 10) % 3 === 0 ? v : ""),
          style: { fontSize: "10px", colors: theme.palette.text.secondary },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        title: { text: "Hour of day", style: { fontSize: "12px", fontWeight: 500, color: theme.palette.text.secondary } },
      },
      yaxis: {
        title: { text: "Sessions", style: { fontSize: "12px", fontWeight: 500, color: theme.palette.text.secondary } },
        labels: { style: { fontSize: "11px", colors: theme.palette.text.secondary } },
      },
      grid: { borderColor: theme.palette.divider, strokeDashArray: 3, xaxis: { lines: { show: false } } },
      tooltip: {
        x: { formatter: (_, { dataPointIndex }) => labels[dataPointIndex] },
        y: { formatter: (v) => `${Number(v).toLocaleString()} sessions` },
      },
    }),
    [labels, totals, max, theme]
  );

  const series = useMemo(() => [{ name: "Sessions", data: totals }], [totals]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (max === 0) {
    return (
      <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}>
        No peak-hour data for the selected period
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ width: "100%", height }}>
        <BaseChart options={options} series={series} type="bar" height="100%" width="100%" />
      </Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Darker bars are peak hours (at least 80% of the busiest hour).
      </Typography>
    </Box>
  );
};

PeakHourBar.propTypes = {
  heatmap: PropTypes.object,
  isLoading: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
};

export default PeakHourBar;
